import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  MessageSquare, 
  Menu, 
  X, 
  LogOut, 
  User,
  Users,
  TrendingUp,
  Target,
  DollarSign,
  Search,
  Mic,
  BarChart3,
  Database,
  FileText,
  Brain,
  Construction,
  Settings,
  MessageCircle,
  BookOpen,
  Clock,
  AlertTriangle,
  Lightbulb,
  Database as DatabaseIcon,
  Award,
  Building2,
  Shield,
  GitBranch,
  Share2,
  FileOutput,
  UserPlus,
  Edit3,
  Highlighter,
  Download,
  BarChart,
  Zap,
  CheckCircle,
  AlertTriangle as AlertTriangleIcon,
  Bot,
  LayoutGrid,
  Wrench,
  PlusCircle,
  Box
} from 'lucide-react';
import { Pin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AGENTS } from '../data/agentCatalog';
import usePinnedAgents from '../hooks/usePinnedAgents';

const demoAdminEmails = ['robert.dong@jobotics.ai', 'skye@jobotics.ai', 'admin@jobotics.ai', 'test@jobotics.ai'];

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  // Pinned agents (user-customizable, persists in localStorage). Renders
  // as a "Pinned" group at the top of the sidebar with one link per
  // pinned agent. Pin/unpin happens from the agent page header.
  const { pinned: pinnedAgentIds } = usePinnedAgents();
  // Custom user-created agents (live in Postgres, fetched at mount).
  // Without this, a pinned custom-agent id never resolves through
  // the static AGENTS array and silently drops from the sidebar.
  // NDA Agent is invitation-only; the server holds the list (NDA_REVIEW_USERS).
  const [ndaAgentAllowed, setNdaAgentAllowed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/contract-qa/nda/access', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setNdaAgentAllowed(Boolean(data?.allowed)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [customAgents, setCustomAgents] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/rsi-agents', { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (cancelled || !data?.agents) return;
        // Map the server row shape to the catalog shape the sidebar
        // expects (id/name/emoji/route).
        setCustomAgents(data.agents.map((a) => ({
          id: a.id,
          name: a.name,
          emoji: '\u{1F9E0}',
          category: a.category,
          route: `/jobotics-agent?agentId=${encodeURIComponent(a.id)}`,
          isUserAgent: true,
        })));
      })
      .catch(() => { /* unauth'd or DB down — sidebar still works for built-ins */ });
    return () => { cancelled = true; };
  }, []);

  // Resolve ids → agent objects against BOTH the built-in catalog and
  // the lazy-loaded custom-agent list. Drop ids that match neither
  // (catalog refactor / deleted custom agent) so the sidebar doesn't
  // show dead entries.
  const pinnedAgents = pinnedAgentIds
    .map((id) => AGENTS.find((a) => a.id === id) || customAgents.find((a) => a.id === id))
    .filter(Boolean);

  // Define allowed users for Sylvan Feed demo
  const allowedSylvanFeedUsers = [
    'rdong@sylvan-inc.com',
    'acumming@sylvan-inc.com', 
    'anita@e-3.tech'
  ];

  // CRM Analyst is now available to all authenticated users

  const navigation = [
    // User Guide retired — sidebar trimmed for demo focus.
    // Jobotics Agent now points at the role-driven Home (`/`) instead of the
    // bare /jobotics-agent surface; from there a user picks one of the
    // recommended agent cards which routes back to /jobotics-agent?agentId=...
    // Agent Library is the role-driven Home page (/) — that's where the
    // recommended + demo + browse-by-category sections live. Custom-agent
    // shortcuts sit just below so users can jump into their workspace
    // or build a new agent without going through Home first.
    { name: 'Agent Library', href: '/', icon: LayoutGrid },
    { name: 'My Agents', href: '/rsi-agents', icon: Wrench },
    { name: 'Build Own Agent', href: '/rsi-agents/new', icon: PlusCircle },
    { name: 'Contract Redline', href: '/contract-redline', icon: Edit3 },
    ...(ndaAgentAllowed ? [{ name: 'NDA Agent', href: '/nda-agent', icon: FileText, highlight: true, highlightText: 'NEW' }] : []),
    // PM Voice Agent retired in Phase 2; mic button now lives inside /chat.
    // AI Answer feature hidden
    // { 
    //   name: 'AI Answer', 
    //   href: '/ai-answer', 
    //   icon: Brain,
    //   children: [
    //     { name: 'Procore Analyst', href: '/chat', icon: MessageSquare },
    //     { name: 'Vista Analyst', href: '/jobsite-deep-dive', icon: BarChart3 },
    //     { name: 'Safety Analyst', href: '/safety-analyst', icon: Shield },
    //     // { name: 'SharePoint Analyst', href: '/25-111-search', icon: Database, highlight: true, highlightText: 'DEMO' },
    //     // { name: 'CRM Analyst', href: '/crm-analyst', icon: DollarSign, highlight: true, highlightText: 'DEMO' }
    //   ]
    // },
    // Procore, Vista, and Safety Analyst features hidden
    // { name: 'Procore Analyst', href: '/chat', icon: MessageSquare },
    // { name: 'Vista Analyst', href: '/jobsite-deep-dive', icon: BarChart3 },
    // { name: 'Safety Analyst', href: '/safety-analyst', icon: Shield },
    // One visibility list for the admin-only demo surfaces (Context Graph, Flow,
    // Code Review, SLD/MEP review). Visibility only — every one of these is
    // ALSO gated server-side. test@ is the env-activated demo guest account.
    // { name: 'AI Workflow', href: '/ai-workflow', icon: GitBranch, highlight: true, highlightText: 'NEW' },
    // { name: 'AI Review', href: '/ai-search', icon: Search },
        // { name: 'AI Report', href: '/ai-report', icon: FileText, highlight: true, highlightText: 'NEW' },
    // { name: 'Vista ERD', href: '/vista-erd', icon: DatabaseIcon }, // Temporarily hidden
    // { name: 'Bidding Intelligence', href: '/bidding-intelligence', icon: Award }, // Temporarily hidden
    // { name: 'Windsor Market Intelligence', href: '/windsor-market-intelligence', icon: Building2 }, // Temporarily hidden
    { name: 'Chat History', href: '/user-chat', icon: MessageCircle },
    // { name: 'AI Whiteboard', href: 'https://jobotics-whiteboard.netlify.app/', icon: TrendingUp, highlight: true, highlightText: 'DEMO', external: true },
    // Only show Sylvan Feed for specific users
    ...(user && allowedSylvanFeedUsers.includes(user.email) ? [
      { name: 'Sylvan Feed', href: '/sylvan-feed', icon: TrendingUp, highlight: true, highlightText: 'DEMO' }
    ] : []),
    // Context Graph — the graph product cloned from the Sylvan portal, served by
    // the server at /graph (outside the SPA). It replaced the vendored Project
    // Context Graph demo at /pcg, which is still served for anyone with the old
    // link but no longer offered here. This list only decides visibility; the
    // graph router gates every endpoint server-side.
    ...(user &&
    demoAdminEmails.includes(
      (user.email || '').toLowerCase()
    )
      ? [{ name: 'Context Graph', href: '/graph/', icon: Share2, external: true, highlight: true, highlightText: 'ADMIN' }]
      : []),
    // Jobotics Flow — admin-only demo surface served by the server at /flow
    // (outside the SPA, so it's an external link). This list only decides what
    // the sidebar shows; server/routes/flow.js gates the page and every endpoint
    // on FLOW_ADMINS, so a direct URL from anyone else still gets refused.
    ...(user &&
    demoAdminEmails.includes(
      (user.email || '').toLowerCase()
    )
      ? [{ name: 'Jobotics Flow', href: '/flow', icon: GitBranch, external: true, highlight: true, highlightText: 'ADMIN' }]
      : []),
    // Electrical Code Review — same shape as Flow: served by the server at
    // /codereview, outside the SPA, and gated server-side on CODEREVIEW_ADMINS
    // (falling back to FLOW_ADMINS). The sidebar entry only decides visibility.
    ...(user &&
    demoAdminEmails.includes(
      (user.email || '').toLowerCase()
    )
      // Points at v2: drop a sheet and it works out what the drawing is, rather than
      // asking for categories up front. v1 is not retired — it is still served at
      // /codereview, still linked from the v2 page's header, and still the exact
      // vector census v2 routes CAD pages to.
      ? [{ name: 'MEP Drawing Review', href: '/codereview/v2', icon: Wrench, external: true, highlight: true, highlightText: 'ADMIN' }]
      : []),
    // CNC & Branch Routing is hidden from the sidebar. The workstream itself is
    // still served at /codereview/cnc behind requireReviewAdmin, so an existing
    // link keeps working; it is just no longer offered here.
    // AI Drawing-to-3D — 2D MEP drawing PDF → interactive 3D reconstruction, copied
    // from the Sylvan portal. Served by the server at /api/drawing-to-3d/viewer/
    // (outside the SPA). Open to any signed-in user, same as on Sylvan.
    { name: 'AI Drawing-to-3D', href: '/api/drawing-to-3d/viewer/', icon: Box, external: true, highlight: true, highlightText: 'NEW' },
    // Usage Dashboard: same allowlist as server (USAGE_DASHBOARD_ADMINS).
    // admin@jobotics.ai is the provisioned email for the admin/jobotics2025
    // password login — added so anyone who signs in that way gets the
    // sidebar link in addition to API access.
    ...(user &&
    demoAdminEmails.includes(
      (user.email || '').toLowerCase()
    )
      ? [{ name: 'Usage Dashboard', href: '/usage-dashboard', icon: BarChart, highlight: true, highlightText: 'ADMIN' }]
      : []),
    // Only show User Registration for admin users
    // ...(user && user.role === 'admin' ? [
    //   { name: 'Registration', href: '/register', icon: UserPlus, highlight: true, highlightText: 'ADMIN' }
    // ] : [])
  ];

  const isActive = (path) => location.pathname === path;

  // Get contextual top bar items based on active tab
  const getTopBarItems = () => {
    switch (location.pathname) {
      case '/ai-search':
        return [
          { icon: FileText, label: 'Document Review', description: 'Comprehensive document analysis' },
          { icon: Brain, label: 'AI Intelligence', description: 'Smart content insights and patterns' },
          { icon: Search, label: 'Knowledge Discovery', description: 'Extract actionable insights from documents' }
        ];
      case '/contract-redline':
        return {
          type: 'how-it-works',
          steps: [
            { number: 1, title: 'Upload Contract', description: 'Upload your PDF or Word document' },
            { number: 2, title: 'AI Analysis', description: 'AI analyzes clauses and identifies risks' },
            { number: 3, title: 'Review Annotations', description: 'Review automatic redline suggestions' },
            { number: 4, title: 'Download Redlined', description: 'Download PDF with all markups' }
          ],
          features: [
            { icon: FileText, label: 'PDF & Word Support' },
            { icon: Highlighter, label: 'Auto Annotations' },
            { icon: AlertTriangleIcon, label: 'Risk Assessment' }
          ]
        };
      case '/ai-answer':
        return [
          { icon: Brain, label: 'Multi-Agent AI', description: 'Orchestrates Vista, Procore & File Search' },
          { icon: BarChart3, label: 'Comprehensive Analysis', description: 'Cross-system data correlation' },
          { icon: Target, label: 'Intelligent Routing', description: 'Query translation & execution' },
          { icon: MessageSquare, label: 'Procore Analysis', description: 'RFI, submittals, safety tracking' },
          { icon: Database, label: 'Vista Analysis', description: 'Costs, budgets, financials' },
          { icon: FileText, label: 'File Search', description: 'Project document analysis' }
        ];
      case '/chat':
        return [
          { icon: AlertTriangle, label: 'RFI Management', description: 'Track RFI status and overdue items' },
          { icon: Shield, label: 'Risk Assessment', description: 'Identify project risks and issues' },
          { icon: FileText, label: 'Submittal Tracking', description: 'Monitor submittal progress and approvals' }
        ];
      case '/jobsite-deep-dive':
        return [
          { icon: Construction, label: 'Vista Analysis', description: 'Project-specific analysis' },
          { icon: BarChart3, label: 'Cost Breakdown', description: 'Detailed cost analysis' },
          { icon: AlertTriangle, label: 'Variance Tracking', description: 'Budget vs actual' }
        ];
      case '/25-111-search':
        return [
          { icon: Database, label: 'Project Documents', description: 'Ford GA Tooling project files' },
          { icon: FileText, label: 'Document Search', description: 'Search within project documents' },
          { icon: Search, label: 'AI-Powered Search', description: 'Intelligent document analysis' }
        ];
      // '/voice-agent' was retired in Phase 2 — voice input is now a mic
      // button inside /chat. The right-rail "how it works" panel for it has
      // been removed along with the route.
      case '/crm-analyst':
        return [
          { icon: DollarSign, label: 'Sales Analysis', description: 'Pipeline and revenue insights' },
          { icon: BarChart3, label: 'Performance Metrics', description: 'Win rates and deal tracking' },
          { icon: Users, label: 'Contact Management', description: 'Customer relationship tracking' },
          { icon: Target, label: 'Pipeline Management', description: 'Opportunity tracking and forecasting' }
        ];
      case '/crm-analyst-new':
        return [
          { icon: Users, label: 'Lead Analysis', description: 'Marketing channel and lead quality insights' },
          { icon: Target, label: 'Conversion Tracking', description: 'Lead-to-customer conversion rates by source' },
          { icon: DollarSign, label: 'Customer Value', description: 'Lifetime value analysis by customer segments' },
          { icon: Clock, label: 'Sales Cycle', description: 'Sales cycle length and influencing factors' },
          { icon: BarChart3, label: 'Sales Performance', description: 'Sales team closing rates and deal sizes' },
          { icon: TrendingUp, label: 'Growth Metrics', description: 'Customer acquisition and retention trends' }
        ];
      // case '/bidding-intelligence': // Temporarily hidden
      //   return [
      //     { icon: Award, label: 'Bid Analysis', description: 'Win rate correlation analysis' },
      //     { icon: TrendingUp, label: 'Performance Metrics', description: 'Client and estimator insights' },
      //     { icon: Target, label: 'Strategic Recommendations', description: 'Data-driven bidding strategy' }
      //   ];
      case '/windsor-market-intelligence':
        return [
          { icon: Building2, label: 'Market Analysis', description: 'Windsor construction market insights' },
          { icon: TrendingUp, label: 'Market Trends', description: 'Quarterly growth and competition analysis' },
          { icon: Target, label: 'Market Opportunities', description: 'Strategic market positioning' }
        ];
      case '/ai-workflow':
        return [
          { icon: GitBranch, label: 'Multi-Node Workflow', description: 'AI creates dynamic execution graphs' },
          { icon: Brain, label: 'Intelligent Orchestration', description: 'LLM plans and executes complex tasks' },
          { icon: Database, label: 'Multiple Data Sources', description: 'Combine Procore, Vista, CRM, Safety, SharePoint' },
          { icon: FileOutput, label: 'Multiple Outputs', description: 'Generate slides, reports, dashboards, charts' }
        ];
      case '/labor-estimator':
        return {
          type: 'how-it-works',
          steps: [
            { number: 1, title: 'Ask Question', description: 'Type your question with material, size, and schedule info' },
            { number: 2, title: 'AI Analysis', description: 'AI analyzes and retrieves labor hour data' },
            { number: 3, title: 'Review Results', description: 'Check the labor hours table and explanation' },
            { number: 4, title: 'Follow-up', description: 'Use suggested questions or ask your own' }
          ],
          features: [
            { icon: FileText, label: 'Materials & Sizes' },
            { icon: Settings, label: 'Connection Types' },
            { icon: Database, label: 'Depths & Soil Conditions' }
          ]
        };
      case '/jobotics-agent': {
        // Agent-specific caption row: read ?agentId from the URL, look up
        // the persona in the shared catalog, and use its name + blurb. Falls
        // back to the generic Jobotics Agent set when no agentId is present.
        const params = new URLSearchParams(location.search);
        const agentId = (params.get('agentId') || '').trim().toLowerCase();
        const agent = agentId ? AGENTS.find((a) => a.id === agentId) : null;
        if (agent) {
          return [
            { label: agent.name, description: agent.blurb, emoji: agent.emoji },
            { icon: Zap, label: 'Plans & runs tasks', description: 'Chooses tools to answer your question and produce files or email' },
            { icon: Search, label: 'Web when needed', description: 'Public web search with sources' }
          ];
        }
        return [
          { icon: Bot, label: 'Jobotics Agent', description: 'A more comprehensive AEC agent — research, documents, labor, deliverables' },
          { icon: Zap, label: 'Plans & runs tasks', description: 'Chooses tools to answer your question and produce files or email' },
          { icon: Search, label: 'Web when needed', description: 'Public web search with sources' }
        ];
      }
      case '/user-guide':
        return [
          { icon: BookOpen, label: 'Getting Started', description: 'Essential guide for construction professionals' },
          { icon: Target, label: 'Choose Your Tool', description: 'Find the right AI tool for your question' },
          { icon: Lightbulb, label: 'Pro Tips', description: 'Get better results with expert techniques' }
        ];
      case '/safety-analyst':
        return [
          { icon: Shield, label: 'Safety Management', description: 'Analyze safety incidents and compliance data' },
          { icon: AlertTriangle, label: 'Risk Assessment', description: 'Track safety risks and incident patterns' },
          { icon: BarChart3, label: 'Safety Analytics', description: 'Monitor safety performance metrics' },
          { icon: Users, label: 'Team Performance', description: 'Analyze field team productivity and best practices' },
          { icon: Clock, label: 'Data Collection', description: 'Track data collection efficiency and accuracy' },
          { icon: FileText, label: 'Compliance Tracking', description: 'Monitor safety compliance and documentation' }
        ];
      case '/register':
        return [
          { icon: UserPlus, label: 'User Registration', description: 'Create new user accounts' },
          { icon: Settings, label: 'Admin Management', description: 'Admin-only access to user creation' },
          { icon: Shield, label: 'Access Control', description: 'Manage user permissions and roles' }
        ];
      default:
        return [
          { icon: TrendingUp, label: 'Project Management', description: 'Track project progress' },
          { icon: Target, label: 'Risk Modeling', description: 'Identify potential risks' },
          { icon: DollarSign, label: 'Financial Insights', description: 'Budget & cost analysis' }
        ];
    }
  };

  const topBarItems = getTopBarItems();

  return (
    <div className="h-screen bg-gray-50 flex">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-ink text-canvas">
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center">
              <img 
                src={process.env.REACT_APP_LOGO_URL}
                alt="Jobotics Logo" 
                className="h-8 w-auto"
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
          
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {/* Mobile: same Pinned group as desktop. Closes the drawer on
                tap so the chosen agent's page actually shows. */}
            {pinnedAgents.length > 0 && (
              <div className="mb-3">
                <div className="px-3 mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-canvas/40">
                  <Pin size={12} className="rotate-45" />
                  <span>Pinned</span>
                </div>
                <div className="space-y-1">
                  {pinnedAgents.map((a) => {
                    const href = a.route && a.route.startsWith('/jobotics-agent')
                      ? a.route
                      : (a.route || `/jobotics-agent?agentId=${a.id}`);
                    return (
                      <Link
                        key={a.id}
                        to={href}
                        onClick={() => setSidebarOpen(false)}
                        className="flex items-center px-3 py-2 rounded-lg text-sm text-canvas/55 hover:bg-white/[0.07] hover:text-canvas"
                      >
                        <span className="mr-3 text-base leading-none">{a.emoji || '📌'}</span>
                        <span className="flex-1 truncate">{a.name.replace(/ Agent$/, '')}</span>
                      </Link>
                    );
                  })}
                </div>
                <div className="mt-3 mx-3 border-t border-white/10" />
              </div>
            )}

            {navigation.map((item) => {
              const Icon = item.icon;
              const hasChildren = item.children && item.children.length > 0;
              const isParentActive = isActive(item.href) || (hasChildren && item.children.some(child => isActive(child.href)));

              return (
                <div key={item.name}>
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors text-canvas/55 hover:bg-white/[0.07] hover:text-canvas"
                    >
                      <Icon size={20} className="mr-3" />
                      <span className="flex-1">{item.name}</span>
                      {item.beta && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-sylvan-100 text-sylvan-800 rounded-full">
                          BETA
                        </span>
                      )}
                      {item.highlight && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-500 text-white rounded-full">
                          {item.highlightText || 'DEMO'}
                        </span>
                      )}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isParentActive
                          ? 'bg-white/10 text-canvas border-r-2 border-sylvan-500'
                          : 'text-canvas/55 hover:bg-white/[0.07] hover:text-canvas'
                      }`}
                    >
                      <Icon size={20} className="mr-3" />
                      <span className="flex-1">{item.name}</span>
                      {item.beta && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-sylvan-100 text-sylvan-800 rounded-full">
                          BETA
                        </span>
                      )}
                      {item.highlight && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-sylvan-600 text-white rounded-full animate-pulse">
                          {item.highlightText ? `🎯 ${item.highlightText}` : '📖 READ'}
                        </span>
                      )}
                    </Link>
                  )}
                  
                  {/* Sub-navigation */}
                  {hasChildren && (
                    <div className="ml-6 mt-1 space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        return (
                          child.comingSoon ? (
                            <div
                              key={child.name}
                              className="flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 cursor-not-allowed"
                            >
                              <ChildIcon size={16} className="mr-2" />
                              <span className="flex-1">{child.name}</span>
                              <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                                SOON
                              </span>
                            </div>
                          ) : (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={() => setSidebarOpen(false)}
                              className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                isActive(child.href)
                                  ? 'bg-white/10 text-canvas border-l-2 border-sylvan-500'
                                  : 'text-canvas/45 hover:bg-white/[0.07] hover:text-canvas'
                              }`}
                            >
                              <ChildIcon size={16} className="mr-2" />
                              <span className="flex-1">{child.name}</span>
                              {child.beta && (
                                <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-sylvan-100 text-sylvan-800 rounded-full">
                                  BETA
                                </span>
                              )}
                            </Link>
                          )
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-ink text-canvas">
          <div className="flex items-center gap-2.5 h-16 px-5 shrink-0">
            {/* Real Jobotics mark on a light chip so the dark logo reads on the
                ink sidebar, with a light wordmark alongside (reference layout). */}
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white shrink-0">
              <img src="/jobotics-logo.png" alt="Jobotics" className="w-7 h-7 object-contain" />
            </span>
            <span className="text-[17px] font-semibold tracking-tight text-canvas">Jobotics</span>
          </div>

          <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
            {/* Pinned agents — user-customizable group, persisted to
                localStorage. Each entry routes to the agent's page (its
                custom route for special agents like Project Heatmap, or
                /jobotics-agent?agentId=... for catalog agents). */}
            {pinnedAgents.length > 0 && (
              <div className="mb-3">
                <div className="px-3 mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-canvas/40">
                  <Pin size={12} className="rotate-45" />
                  <span>Pinned</span>
                </div>
                <div className="space-y-1">
                  {pinnedAgents.map((a) => {
                    const href = a.route && a.route.startsWith('/jobotics-agent')
                      ? a.route
                      : (a.route || `/jobotics-agent?agentId=${a.id}`);
                    const active = (location.pathname + location.search) === href
                      || (location.search.includes(`agentId=${a.id}`) && location.pathname === '/jobotics-agent');
                    return (
                      <Link
                        key={a.id}
                        to={href}
                        title={a.name}
                        className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                          active
                            ? 'bg-white/10 text-canvas border-r-2 border-sylvan-500'
                            : 'text-canvas/55 hover:bg-white/[0.07] hover:text-canvas'
                        }`}
                      >
                        <span className="mr-3 text-base leading-none">{a.emoji || '📌'}</span>
                        <span className="flex-1 truncate">{a.name.replace(/ Agent$/, '')}</span>
                      </Link>
                    );
                  })}
                </div>
                <div className="mt-3 mx-3 border-t border-white/10" />
              </div>
            )}

            {navigation.map((item) => {
              const Icon = item.icon;
              const hasChildren = item.children && item.children.length > 0;
              const isParentActive = isActive(item.href) || (hasChildren && item.children.some(child => isActive(child.href)));

              return (
                <div key={item.name}>
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors text-canvas/55 hover:bg-white/[0.07] hover:text-canvas"
                    >
                      <Icon size={20} className="mr-3" />
                      <span className="flex-1">{item.name}</span>
                      {item.beta && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-sylvan-100 text-sylvan-800 rounded-full">
                          BETA
                        </span>
                      )}
                      {item.highlight && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-500 text-white rounded-full">
                          {item.highlightText || 'DEMO'}
                        </span>
                      )}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isParentActive
                          ? 'bg-white/10 text-canvas border-r-2 border-sylvan-500'
                          : 'text-canvas/55 hover:bg-white/[0.07] hover:text-canvas'
                      }`}
                    >
                      <Icon size={20} className="mr-3" />
                      <span className="flex-1">{item.name}</span>
                      {item.beta && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-sylvan-100 text-sylvan-800 rounded-full">
                          BETA
                        </span>
                      )}
                      {item.highlight && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-sylvan-600 text-white rounded-full animate-pulse">
                          {item.highlightText ? `🎯 ${item.highlightText}` : '📖 READ'}
                        </span>
                      )}
                    </Link>
                  )}
                  
                  {/* Sub-navigation */}
                  {hasChildren && (
                    <div className="ml-6 mt-1 space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        return (
                          child.comingSoon ? (
                            <div
                              key={child.name}
                              className="flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 cursor-not-allowed"
                            >
                              <ChildIcon size={16} className="mr-2" />
                              <span className="flex-1">{child.name}</span>
                              <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                                SOON
                              </span>
                            </div>
                          ) : (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                isActive(child.href)
                                  ? 'bg-white/10 text-canvas border-l-2 border-sylvan-500'
                                  : 'text-canvas/45 hover:bg-white/[0.07] hover:text-canvas'
                              }`}
                            >
                              <ChildIcon size={16} className="mr-2" />
                              <span className="flex-1">{child.name}</span>
                              {child.beta && (
                                <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-sylvan-100 text-sylvan-800 rounded-full">
                                  BETA
                                </span>
                              )}
                            </Link>
                          )
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Promo card — editorial brand note (reference sidebar) */}
          <div className="mx-3 mb-3 rounded-lg bg-white/[0.06] border border-white/10 p-4">
            <div className="text-sm font-medium text-canvas leading-snug">Built for MEP.<br />Powered by AI.</div>
            <div className="text-[12px] text-canvas/55 mt-1.5 leading-relaxed">Jobotics connects your data, documents, and workflows — so you can build better.</div>
          </div>

          {/* User section */}
          <div className="border-t border-white/10 p-4">
            <div className="flex items-center">
              <div className="h-9 w-9 bg-gradient-to-br from-sylvan-500 to-sylvan-700 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0">
                {user?.firstName ? user.firstName.charAt(0).toUpperCase() : user?.username?.charAt(0)?.toUpperCase() || 'U'}
                {user?.lastName ? user.lastName.charAt(0).toUpperCase() : ''}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-canvas truncate">
                  {user?.name || user?.username || 'User'}
                </p>
                {user?.email && (
                  <p className="text-xs text-canvas/45 truncate">{user.email}</p>
                )}
              </div>
              <button
                onClick={logout}
                className="text-canvas/40 hover:text-canvas transition-colors shrink-0"
                title="Logout"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex-1 flex flex-col">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
          {topBarItems && topBarItems.type === 'how-it-works' ? (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
              <div className="px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="lg:hidden text-gray-400 hover:text-gray-600"
                  >
                    <Menu size={24} />
                  </button>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 flex-1">
                    {topBarItems.steps.map((step, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xs">
                          {step.number}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs sm:text-sm leading-tight">
                            <span className="font-semibold text-gray-900">{step.title}</span>
                            <span className="text-gray-600 ml-1">{step.description}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Feedback Button - Always visible, rightmost, same line */}
                  <button
                    onClick={() => setFeedbackModalOpen(true)}
                    className="flex items-center justify-center p-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
                    title="Provide Feedback"
                  >
                    <MessageSquare size={20} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-400 hover:text-gray-600"
              >
                <Menu size={24} />
              </button>
              
              {/* Left-aligned top bar items */}
              <div className="hidden sm:flex items-center space-x-4 flex-1">
                {Array.isArray(topBarItems) && topBarItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-center text-sm text-gray-500 group relative">
                      {/* Prefer emoji when item carries one (used by agent
                          personas so the caption row matches the persona),
                          otherwise the supplied lucide icon. */}
                      {item.emoji ? (
                        <span aria-hidden className="mr-1 text-base leading-none">{item.emoji}</span>
                      ) : Icon ? (
                        <Icon size={16} className="mr-1" />
                      ) : null}
                      <span>{item.label}</span>
                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                        {item.description}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Feedback Button - Always visible, rightmost */}
              <button
                onClick={() => setFeedbackModalOpen(true)}
                className="flex items-center justify-center p-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ml-auto"
                title="Provide Feedback"
              >
                <MessageSquare size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="h-full px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* Feedback Modal */}
      {feedbackModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setFeedbackModalOpen(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Feedback</h3>
                  <button
                    onClick={() => setFeedbackModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className="w-full">
                  <iframe 
                    width="100%" 
                    height="480px" 
                    src="https://forms.office.com/Pages/ResponsePage.aspx?id=DQSIkWdsW0yxEjajBLZtrQAAAAAAAAAAAAO__UAZhBRUNkcwQUlBMEVFMlY3SDlYS1EwSkFVQVY4MS4u&embed=true" 
                    frameBorder="0" 
                    marginWidth="0" 
                    marginHeight="0" 
                    style={{ border: 'none', maxWidth: '100%', maxHeight: '100vh' }} 
                    allowFullScreen 
                    webkitallowfullscreen="true" 
                    mozallowfullscreen="true" 
                    msallowfullscreen="true"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout; 